import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Mail, HeartHandshake, Sparkles, Quote } from "lucide-react";
import { waLink, PRACTITIONER } from "../lib/api";

const SERVICES_PREVIEW = [
  { title: "Individual Counselling", desc: "One-on-one sessions tailored to your unique journey." },
  { title: "Anxiety Support", desc: "Tools to understand, soften, and live alongside anxiety." },
  { title: "Stress Management", desc: "Find your way back to steadiness, gently." },
];

const TESTIMONIALS = [
  { name: "A.S.", text: "I felt heard for the first time in years. Sudiksha holds space without judgment.", role: "Client, 28" },
  { name: "R.K.", text: "Practical, warm, and deeply insightful. Sessions became something I looked forward to.", role: "Client, 34" },
  { name: "M.D.", text: "Helped me untangle thoughts I didn't even know I was carrying.", role: "Client, 22" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{
          background: "radial-gradient(60% 60% at 70% 30%, rgba(226,222,232,0.55) 0%, rgba(213,225,232,0.25) 40%, rgba(252,250,248,0) 80%)"
        }}/>
        <div className="container-soft grid lg:grid-cols-12 gap-10 lg:gap-16 py-20 md:py-28 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: "var(--primary-deep)" }}>Psychologist · Counsellor</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-6" style={{ color: "var(--text-primary)" }}>
              Supporting Your <em className="not-italic" style={{ color: "var(--primary-deep)"}}>Mental & Emotional</em> Well-Being
            </h1>
            <p className="text-lg max-w-xl mb-8 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              I offer a safe, empathetic, and non-judgmental space for individuals seeking emotional support and personal growth. Healing begins with one honest conversation.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/booking" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm transition-colors hover:opacity-90" style={{ background: "var(--primary)", color: "#fff" }} data-testid="hero-book-btn">
                Book a Consultation <ArrowRight size={16}/>
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm transition-colors" style={{ background: "var(--accent-beige)", color: "var(--text-primary)" }} data-testid="hero-contact-btn">
                <Mail size={16}/> Contact Me
              </Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm transition-colors border" style={{ borderColor: "var(--border-soft)", color: "var(--text-primary)" }} data-testid="hero-whatsapp-btn">
                <MessageCircle size={16}/> WhatsApp Me
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up">
            <div className="relative max-w-[400px] mx-auto">
              <div className="absolute -inset-4 rounded-[2.5rem]" style={{ background: "var(--accent-lavender)", opacity: 0.5 }}/>
              <img
                src="/images/sudiksha.jpg"
                alt="Sudiksha Kulharia — Psychologist & Counsellor"
                className="relative rounded-[2rem] w-full object-cover aspect-[4/5] shadow-sm"
                data-testid="hero-portrait"
              />
              <div className="absolute -bottom-6 -left-6 rounded-2xl px-5 py-4 shadow-sm flex items-center gap-3" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)" }}>
                <HeartHandshake size={20} style={{ color: "var(--primary-deep)" }}/>
                <div>
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--primary-deep)" }}>Now Accepting</div>
                  <div className="text-sm" style={{ color: "var(--text-primary)" }}>New Clients · Online Sessions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="container-soft py-16 md:py-20">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: Sparkles, title: "Empathetic", body: "Sessions led with deep listening and warmth." },
            { icon: HeartHandshake, title: "Non-judgmental", body: "Bring whatever is on your mind — there's no wrong answer." },
            { icon: MessageCircle, title: "Confidential", body: "Your privacy is protected at every step." },
          ].map((b) => (
            <div key={b.title} className="rounded-3xl p-8" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)" }} data-testid={`pillar-${b.title.toLowerCase()}`}>
              <b.icon size={22} style={{ color: "var(--primary-deep)" }}/>
              <h3 className="font-display text-2xl mt-4 mb-2" style={{ color: "var(--text-primary)" }}>{b.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="container-soft py-16 md:py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--primary-deep)" }}>What I Offer</div>
            <h2 className="font-display text-4xl sm:text-5xl" style={{ color: "var(--text-primary)" }}>Areas of Support</h2>
          </div>
          <Link to="/services" className="text-sm inline-flex items-center gap-2" style={{ color: "var(--primary-deep)" }} data-testid="home-view-services">
            View all services <ArrowRight size={14}/>
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES_PREVIEW.map((s) => (
            <div key={s.title} className="rounded-3xl p-8 transition-all hover:-translate-y-1 hover:shadow-md" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)" }} data-testid={`service-card-${s.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <h3 className="font-display text-2xl mb-3" style={{ color: "var(--text-primary)" }}>{s.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20" style={{ background: "var(--bg-secondary)" }}>
        <div className="container-soft">
          <div className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--primary-deep)" }}>Kind Words</div>
          <h2 className="font-display text-4xl sm:text-5xl mb-12" style={{ color: "var(--text-primary)" }}>From Those I've Walked With</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-3xl p-8 backdrop-blur-sm" style={{ background: "rgba(226,222,232,0.4)", border: "1px solid rgba(255,255,255,0.6)" }} data-testid={`testimonial-${i}`}>
                <Quote size={22} style={{ color: "var(--primary-deep)" }}/>
                <p className="my-4 leading-relaxed" style={{ color: "var(--text-primary)" }}>"{t.text}"</p>
                <div className="text-sm" style={{ color: "var(--text-secondary)" }}>— {t.name} · {t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-soft py-20 md:py-24 text-center">
        <h2 className="font-display text-4xl sm:text-5xl mb-4" style={{ color: "var(--text-primary)" }}>Ready to begin?</h2>
        <p className="max-w-xl mx-auto mb-8" style={{ color: "var(--text-secondary)" }}>
          Reaching out is a meaningful first step. I'd be honoured to walk alongside you.
        </p>
        <Link to="/booking" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm" style={{ background: "var(--primary)", color: "#fff" }} data-testid="cta-book-btn">
          Book a Consultation <ArrowRight size={16}/>
        </Link>
      </section>
    </div>
  );
}
